import { useEffect, useRef, useState } from "react";
import useDocumentTitle from "@/hooks/use-document-title";

// 2.4.7 Focus Visible — AA: scoped to `:focus-visible`, not plain `:focus`, same as
// every other focus ring in this app. An earlier revision forced this ring on
// unconditionally, reasoning that the browser's own heuristic would suppress it right
// after the pointer interaction that triggered the navigation — but in practice that
// heuristic suppression is exactly what a member wants here: a route change triggered
// by clicking a sidebar/menu link is the overwhelmingly common case, and painting a
// bright ring around the new heading on every single mouse-driven navigation reads as
// a stray highlight rather than a focus indicator. Tab/keyboard-driven navigation (and
// a screen reader's virtual cursor) still reliably match `:focus-visible`, which is the
// one case this ring exists for.
const FOCUS_RING_CLASSES = [
  "focus-visible:outline-none",
  "focus-visible:ring-2",
  "focus-visible:ring-ring",
  "focus-visible:ring-offset-2",
];

/**
 * Finds the current page's <main>, and within it the page's own <h1> if it has one, and
 * moves focus there.
 *
 * Prefers the heading over <main> itself: <main> carries no accessible name on most
 * routes, so focusing it announces only "main landmark" with nothing to say what's on
 * screen. The heading says both "you've arrived" and "here's what this is" in one shot.
 * Falls back to <main> for the rare route that has none.
 *
 * Neither is focusable by default; tabindex="-1" makes it programmatically focusable
 * without adding it to the Tab order.
 */
export function focusMainContent() {
  const main = document.querySelector("main");
  if (!main) return;
  const target = main.querySelector<HTMLElement>("h1") ?? main;
  target.setAttribute("tabindex", "-1");
  // `:focus-visible` itself now decides whether the ring shows, so unlike the
  // unconditional classes this used to be, there is nothing to remove on blur.
  target.classList.add(...FOCUS_RING_CLASSES);
  target.focus();
  // jsdom does not implement scrollIntoView, hence the optional call.
  target.scrollIntoView?.();
}

/**
 * Renders the things every single-page app owes assistive technology on navigation, and
 * nothing visible.
 *
 * 1. **Skip link — WCAG 2.4.1 Bypass Blocks (Level A).** No skip link existed anywhere in
 *    this app. Authenticated pages render a persistent sidebar and header, so a keyboard
 *    user had to tab through the whole navigation on *every* route before reaching the
 *    content they came for. The link is visually hidden until focused, which is the point:
 *    it must be the first thing Tab reaches and it must become visible when it is.
 *
 * 2. **Focus moves to the new page on every navigation — WCAG 2.4.3 Focus Order (Level A).**
 *    Clicking a sidebar/menu link is what most members actually do, and React Router swaps
 *    the view without a page load, so focus simply stayed on the clicked link — the member
 *    then had to tab through the *rest* of the menu to reach the content they just
 *    navigated to. The skip link alone doesn't fix this: by the time you're deep in the
 *    menu, the skip link sits earlier in the tab order than where you are, so reaching it
 *    means tabbing backwards through the whole menu instead of forwards past it. Moving
 *    focus automatically on every route change (not just on a manual skip-link click) is
 *    what actually closes the gap. A page that wants a more specific initial focus target
 *    (e.g. a search field) can still move focus itself in its own mount effect — that runs
 *    after this one, so it wins.
 *
 * 3. **Route announcement — WCAG 4.1.3 Status Messages (Level AA).** The polite region
 *    below is mounted once and only its *text* changes, which is what makes it announce —
 *    a live region inserted into the DOM already containing its message stays silent.
 *
 * `useDocumentTitle` (WCAG 2.4.2 Page Titled — Level A) is called here too, so one mount
 * covers the title, the announcement and the focus target.
 */
export function RouteAnnouncer() {
  const pageTitle = useDocumentTitle();
  const [announcement, setAnnouncement] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Don't move focus or announce on the first paint — the browser's own initial focus
    // (and the page title) already cover arrival, and doing it again here would race the
    // initial render as noise, or steal focus from a skip link the member just used.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    focusMainContent();
    setAnnouncement(`${pageTitle} page`);
  }, [pageTitle]);

  const skipToContent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    focusMainContent();
  };

  return (
    <>
      <a
        href="#main-content"
        onClick={skipToContent}
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>

      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </>
  );
}
